
import { useEffect, useState } from 'react';
import {
    Box,
    Avatar,
    Heading,
    Text,
    Flex,
    Icon,
    Spinner, TableContainer, Table, Tr, Th, Td, Tbody, Thead,
    useColorModeValue, SimpleGrid, Divider, Image
} from '@chakra-ui/react';
import { EmailIcon, PhoneIcon } from '@chakra-ui/icons';
import { FaUserTag, FaRegCalendarAlt, FaFilePdf } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout'
import config from '../../constants/config';
import UserService from '../../services/UserService';
import { formatDate, getGender } from '../../utils/helper';

const UserDetail = () => {

    const backendUrl = config.apiBaseUrl;

    const { uid } = useParams();
    const [user, setUser] = useState(null);
    const [contact, setContact] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {

        const fetchUser = async () => {
            let params = {
                uId: uid
            }
            try {
                const response = await UserService.getUserDetail(params);
                setUser(response.data);
            } catch (error) {
                console.error('Error fetching user:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
        getEmergency();
    }, [uid]);

    const getEmergency = async () => {

        setContact([])
        try {
            const response = await UserService.getEmergencyDetail(uid);
            setContact(response);
        } catch (error) {
            console.error('Error fetching user:', error);
        }
    }

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="80vh">
                <Spinner size="xl" />
            </Flex>
        );
    }

    if (!user) {
        return (
            <Box p={6} textAlign="center">
                <Text color="red.500">User not found</Text>
            </Box>
        );
    }

    return (
        <AdminLayout>
            <Box maxW="1000px" mx="auto" px={4}>
                <Box
                    bg="white"
                    p={6}
                    rounded="xl"
                    boxShadow="md"
                    mb={3}
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <Flex direction="column" align="center" mb={6}>
                        <Avatar
                            size="2xl"
                            name={`${user.FName} ${user.LName}`}
                            src={`${backendUrl}/profile/${user.ProfilePic}`}
                            mb={4}
                            border="3px solid white"
                            boxShadow="lg"
                        />
                        <Heading size="lg" textAlign="center">
                            {user.FName} {user.LName}
                        </Heading>
                        <Text fontSize="md" color="gray.500">
                            @{user.uId}
                        </Text>
                    </Flex>

                    <Divider mb={6} />

                    <SimpleGrid columns={[1, 1, 2]} spacing={4} mt={4} placeItems="center">
                        <Flex align="center" gap={3}>
                            <Icon as={EmailIcon} boxSize={5} color="blue.500" />
                            <Text>{user.Email}</Text>
                        </Flex>

                        <Flex align="center" gap={3}>
                            <Icon as={PhoneIcon} boxSize={5} color="green.500" />
                            <Text>{user.MobileNo}</Text>
                        </Flex>

                        <Flex align="center" gap={3}>
                            <Icon as={FaUserTag} boxSize={5} color="purple.500" />
                            <Text>{getGender(user.Gender)}</Text>
                        </Flex>

                        <Flex align="center" gap={3}>
                            <Icon as={FaRegCalendarAlt} boxSize={5} color="orange.500" />
                            <Text>{formatDate(user.DOB)}</Text>
                        </Flex>
                    </SimpleGrid>
                </Box>

                <Box
                    bg="white"
                    p={6}
                    rounded="xl"
                    boxShadow="md"
                    mb={3}
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <Heading size="sm" mb={4}>Documents</Heading>
                    <SimpleGrid columns={[1, 2]} spacing={6}>
                        {/* Aadhar */}
                        <Box borderWidth="1px" borderRadius="md" p={4} boxShadow="md">
                            <Text fontWeight="bold" mb={2}>Aadhar</Text>
                            {user.AadharFile?.endsWith('.pdf') ? (
                                <Flex align="center" gap={2}>
                                    <Icon as={FaFilePdf} color="red.500" boxSize={6} />
                                    <a
                                        href={`${backendUrl}/aadhar/${user.Aadhar}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#3182CE' }}
                                    >
                                        View PDF
                                    </a>
                                </Flex>
                            ) : (
                                <a
                                    href={`${backendUrl}/aadhar/${user.Aadhar}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#3182CE' }}
                                >
                                    <Image
                                        src={`${backendUrl}/aadhar/${user.Aadhar}`}
                                        alt="Aadhar"
                                        maxH="200px"
                                        borderRadius="md"
                                        objectFit="contain"
                                    />
                                </a>
                            )}
                        </Box>

                        {/* License */}
                        <Box borderWidth="1px" borderRadius="md" p={4} boxShadow="md">
                            <Text fontWeight="bold" mb={2}>License</Text>
                            {user.LicenseFile?.endsWith('.pdf') ? (
                                <Flex align="center" gap={2}>
                                    <Icon as={FaFilePdf} color="red.500" boxSize={6} />
                                    <a
                                        href={`${backendUrl}/license/${user.Licence}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#3182CE' }}
                                    >
                                        View PDF
                                    </a>
                                </Flex>
                            ) : (
                                <a
                                    href={`${backendUrl}/license/${user.Licence}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#3182CE' }}
                                >
                                    <Image
                                        src={`${backendUrl}/license/${user.Licence}`}
                                        alt="License"
                                        maxH="200px"
                                        borderRadius="md"
                                        objectFit="contain"
                                    />
                                </a>
                            )}
                        </Box>

                        {/* PAN */}
                        <Box borderWidth="1px" borderRadius="md" p={4} boxShadow="md">
                            <Text fontWeight="bold" mb={2}>PAN</Text>
                            {user.PANFile?.endsWith('.pdf') ? (
                                <Flex align="center" gap={2}>
                                    <Icon as={FaFilePdf} color="red.500" boxSize={6} />
                                    <a
                                        href={`${backendUrl}/pan/${user.PAN}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#3182CE' }}
                                    >
                                        View PDF
                                    </a>
                                </Flex>
                            ) : (
                                <a
                                    href={`${backendUrl}/pan/${user.PAN}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#3182CE' }}
                                >
                                    <Image
                                        src={`${backendUrl}/pan/${user.PAN}`}
                                        alt="PAN"
                                        maxH="200px"
                                        borderRadius="md"
                                        objectFit="contain"
                                    />
                                </a>
                            )}
                        </Box>
                    </SimpleGrid>
                </Box>

                <Box
                    bg="white"
                    p={6}
                    rounded="xl"
                    boxShadow="md"
                    mb={3}
                    border="1px solid"
                    borderColor="gray.200"
                >
                <Heading size="sm" mb={4}>Emergency Contacts</Heading>
                    <TableContainer overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.100">
                                <Tr>
                                    <Th px={2} py={1} >Name</Th>
                                    <Th px={2} py={1} >MobileNo</Th>
                                    <Th px={2} py={1} >Relation</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {contact.map((row) => (
                                    <Tr key={row.UDetNo}>
                                        <Td px={2} py={1}>{row.EmConName}</Td>
                                        <Td px={2} py={1}>{row.EmConNo}</Td>
                                        <Td px={2} py={1}>{row.Relation}</Td>

                                    </Tr>

                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </Box>

            </Box>

        </AdminLayout>
    )
}

export default UserDetail
